import { createSlice, current } from "@reduxjs/toolkit";
import { generateUUID } from "../utils/uuid";
import { emptyVM } from "../utils/workflowViewModel";
import { snapshotVM, recordDiscrete, recordConfig, recordHistory, resetHistory } from "./workflowHistory";

// ─── Helper: rebuild activities from nodes ────────────────────────────────

function rebuildActivitiesFromNodes(nodes) {
  const activeActivities = [];
  const seen = new Set();
  nodes.forEach(node => {
    if (node.type === 'initNode') {
      const uid = node.data.uid || node.id.replace("init-", "");
      if (!seen.has(uid)) {
        seen.add(uid);
        activeActivities.push({
          uid,
          name: node.data.label || node.data.name || "Init",
          type: 'INIT'
        });
      }
    } else if (node.data && node.data.forms) {
      node.data.forms.forEach(form => {
        if (!seen.has(form.uid)) {
          seen.add(form.uid);
          activeActivities.push({
            uid: form.uid,
            name: form.label,
            type: form.type || 'NORMAL'
          });
        }
      });
    }
  });
  return activeActivities;
}

// ─── view-model helpers (operate on the Immer draft's vm) ─────────────────
// Read the current vm as plain objects (xyflow helpers + rebuild need plain
// arrays, not Immer drafts).
function vmNodes(state) {
  const vm = current(state.vm);
  return vm.nodeOrder.map((id) => vm.nodesById[id]);
}
function vmEdges(state) {
  const vm = current(state.vm);
  return vm.edgeOrder.map((id) => vm.edgesById[id]);
}
// Replace an order array only when the sequence actually differs. Assigning a
// fresh array unconditionally marks the draft modified, which is what made every
// write mint a new vm even when nothing moved.
function writeOrder(vm, key, nextOrder) {
  const prev = vm[key];
  if (prev.length === nextOrder.length && nextOrder.every((id, i) => prev[i] === id)) return;
  vm[key] = nextOrder;
}

// Merge into the keyed map in place rather than clearing it first.
//
// This is the difference between "the vm changed" and "the vm was rewritten".
// `state.vm.nodesById = {}` marks the draft modified unconditionally, so a write
// that changed nothing still produced a new `nodesById` — which re-ran
// selectNodes (a fresh n-element array), selectNodeLabelById and
// selectNodeTypeById, and re-rendered all 25 selectNodes consumers. React Flow
// emits plenty of such no-op writes (dimension events on measurement, select
// noise, post-save reloads).
//
// Assigning key-by-key lets immer do its own equality check: re-assigning the
// value already at a key is a no-op, so an unchanged write leaves the whole vm
// reference intact and nothing re-renders. Node objects arrive from
// applyNodeChanges, which preserves the identity of untouched nodes, so a drag
// touches exactly one key.
function mergeById(map, items, prepare) {
  const seen = new Set();
  for (const item of items) {
    seen.add(item.id);
    map[item.id] = prepare ? prepare(item) : item;
  }
  // Collect first: deleting while iterating a draft's keys is not safe.
  for (const id of Object.keys(map)) {
    if (!seen.has(id)) delete map[id];
  }
}

function writeNodes(state, nodes) {
  mergeById(state.vm.nodesById, nodes);
  writeOrder(state.vm, "nodeOrder", nodes.map((n) => n.id));
}

// Edges always carry type + data (the default-fill setEdges has always applied).
// Fill only when a default is genuinely missing: unconditionally spreading every
// edge into `{ ...e }` replaced EVERY edge's identity on ANY edge write, so one
// edge edit re-rendered every edge component and all 22 selectEdges consumers.
function fillEdgeDefaults(e) {
  if (e.type && e.data) return e;
  return { ...e, type: e.type || "custom", data: e.data || { activities: [] } };
}
function writeEdges(state, edges) {
  mergeById(state.vm.edgesById, edges, fillEdgeDefaults);
  writeOrder(state.vm, "edgeOrder", edges.map((e) => e.id));
}
function findNode(state, id) {
  return state.vm.nodesById[id];
}
// Selection is held as an ID and the node/edge is derived at read time
// (selectors.js), so there is no copy to keep in sync — a data edit is picked up
// for free. All that remains is dropping a selection whose target is gone.
// Two map lookups on the draft; the old refresh materialised the ENTIRE vm via
// immer's current() to re-read one entry.
function pruneSelection(state) {
  if (state.selectedNodeId && !state.vm.nodesById[state.selectedNodeId]) state.selectedNodeId = null;
  if (state.selectedEdgeId && !state.vm.edgesById[state.selectedEdgeId]) state.selectedEdgeId = null;
}

// ─── Workflow Slice (core diagram + data) ─────────────────────────────────

export const workflowSlice = createSlice({
  name: "workflow",
  initialState: {
    vm: { nodesById: {}, nodeOrder: [], edgesById: {}, edgeOrder: [], activities: [] },
    history: { past: [], future: [] },
    _coalesceKey: null,   // active config-edit coalesce key, or null
    _dragging: false,     // mid node-drag (suppresses repeat captures)
    workflow: null,
    workflowDataFields: [],
    activityDataFields: [],
    activityPermissions: [],
    activityTriggers: [],
    activityPrefills: {},
    activityPrefillFlow: null,
    currentOrg: null,
    currentPipelineGroup: null,
    // IDs, not node/edge copies. See pruneSelection above.
    selectedNodeId: null,
    selectedEdgeId: null,
    // UIDs of activities created locally but not yet committed by the
    // next diagram save. Used to gate the ActivityDataPanel so it doesn't
    // 404 when the user opens an activity that only exists in Redux.
    unsavedActivityUIDs: [],
  },
  reducers: {
    resetWorkflow: (state) => {
      resetHistory(state);
      state.vm = emptyVM();
      // Clear the header workflow metadata too, so switching versions while on
      // a non-diagram tab doesn't leave the previous workflow's name showing in
      // TopNavBar. The version-keyed effect in Editor.js repopulates it.
      state.workflow = null;
      state.selectedNodeId = null;
      state.selectedEdgeId = null;
      state.workflowDataFields = [];
      state.activityDataFields = [];
      state.activityPrefills = {};
      state.activityPrefillFlow = null;
      state.unsavedActivityUIDs = [];
    },
    // Called after a successful diagram save — every locally-created
    // activity has now been INSERTed by UpdateCoreDiagram via its UID.
    markActivitiesSaved: (state) => {
      state.unsavedActivityUIDs = [];
    },
    // Bulk replace the whole graph (load / reload / AI apply / reset / post-save
    // sync). Phase 2 makes this also reset undo history.
    setGraph: (state, action) => {
      resetHistory(state);
      const { nodes = [], edges = [], activities = [] } = action.payload || {};
      // Direct writes (consistent with every other reducer): writeEdges applies
      // the type/data default-fill that the old setEdges did. No intermediate
      // normalize() — that left edges in a half-written state before writeEdges.
      state.vm = emptyVM();
      state.vm.activities = [...activities];
      writeNodes(state, nodes);
      writeEdges(state, edges);
      pruneSelection(state);
    },
    setNodes: (state, action) => {
      writeNodes(state, action.payload);
      pruneSelection(state);
    },
    // RF node changes from the canvas. History: capture once on drag-start and
    // on remove; never on select/dimension. Swimlane resizes persist into data.
    // Payload: { changes, nextNodes } — applyNodeChanges is done in the component.
    graphNodesChanged: (state, action) => {
      const { changes, nextNodes } = action.payload;
      const hasRemove = changes.some((c) => c.type === "remove");
      const dragMoving = changes.some((c) => c.type === "position" && c.dragging);
      const dragEnded = changes.some((c) => c.type === "position" && c.dragging === false);
      if (hasRemove) {
        recordDiscrete(state);
      } else if (dragMoving && !state._dragging) {
        recordHistory(state);
        state._dragging = true;
        state._coalesceKey = null;
      }
      if (dragEnded) state._dragging = false;
      // A selection click ends a config-edit context: break the coalesce window
      // so editing the same field again after clicking away is a fresh undo step
      // (dimension/measurement noise does NOT break it).
      if (changes.some((c) => c.type === "select")) state._coalesceKey = null;

      let next = nextNodes;
      const resizes = changes.filter((c) => c.type === "dimensions" && c.dimensions);
      for (const resize of resizes) {
        const idx = next.findIndex((n) => n.id === resize.id);
        if (idx !== -1 && next[idx].type === "swimlaneNode") {
          next[idx] = {
            ...next[idx],
            data: { ...next[idx].data, width: resize.dimensions.width, height: resize.dimensions.height },
          };
        }
      }
      writeNodes(state, next);
      pruneSelection(state);
    },
    // RF edge changes from the canvas. History: capture on remove/add only.
    // Payload: { changes, nextEdges } — applyEdgeChanges is done in the component.
    graphEdgesChanged: (state, action) => {
      const { changes, nextEdges } = action.payload;
      if (changes.some((c) => c.type === "remove" || c.type === "add")) recordDiscrete(state);
      writeEdges(state, nextEdges);
    },
    setEdges: (state, action) => {
      recordDiscrete(state);
      writeEdges(state, action.payload);
    },
    setActivities: (state, action) => {
      state.vm.activities = action.payload || [];
    },
    addActivity: (state, action) => {
      recordDiscrete(state);
      const activity = action.payload;
      if (!state.vm.activities.some((a) => a.uid === activity.uid)) {
        state.vm.activities.push({
          uid: activity.uid,
          name: activity.name,
          type: activity.type || "NORMAL",
        });
      }
      if (activity.uid && !state.unsavedActivityUIDs.includes(activity.uid)) {
        state.unsavedActivityUIDs.push(activity.uid);
      }
    },
    addNode: (state, action) => {
      recordDiscrete(state);
      const node = action.payload;
      state.vm.nodesById[node.id] = node;
      state.vm.nodeOrder.push(node.id);
    },
    updateNodeData: (state, action) => {
      const { id, data } = action.payload;
      recordConfig(state, `${id}:${Object.keys(data).join(",")}`);
      const node = findNode(state, id);
      if (node) {
        node.data = { ...node.data, ...data };

        // Sync activity name if it's an INIT node and label changed
        if (node.type === "initNode" && data.label) {
          const uid = node.data.uid || node.id.replace("init-", "");
          const act = state.vm.activities.find((a) => a.uid === uid);
          if (act) act.name = data.label;
        }

      }
    },
    updateEdgeData: (state, action) => {
      const { id, data } = action.payload;
      recordConfig(state, `${id}:${Object.keys(data).join(",")}`);
      const edge = state.vm.edgesById[id];
      if (edge) {
        edge.data = { ...edge.data, ...data };
      }
    },
    deleteNode: (state, action) => {
      recordDiscrete(state);
      const id = action.payload;
      const nextNodes = vmNodes(state).filter((n) => n.id !== id);
      const nextEdges = vmEdges(state).filter((e) => e.source !== id && e.target !== id);
      writeNodes(state, nextNodes);
      writeEdges(state, nextEdges);
      pruneSelection(state);
      state.vm.activities = rebuildActivitiesFromNodes(nextNodes);
    },
    addFormToState: (state, action) => {
      recordDiscrete(state);
      const { stateId, formData } = action.payload;
      const stateNode = findNode(state, stateId);
      if (stateNode) {
        if (!stateNode.data.forms) stateNode.data.forms = [];
        // A form's `id` must equal its `uid` — that's the convention every other
        // path uses (DB reload in transformWorkflow, addExistingActivitiesToState).
        // A new activity reused in a second state via "Add Existing" gets a form
        // there with id === uid; if the original (this) form carried a divergent
        // id (e.g. `act-<ts>`), exportWorkflow would key the SAME activity under
        // two ids and emit it twice, and exportToApiPayload's clone-uid guard
        // would then split the shared uid into two — creating a duplicate row.
        const uid = formData.activity_uid || formData.uid || generateUUID();
        const newForm = {
          ...formData,
          id: formData.id || uid,
          uid,
        };
        stateNode.data.forms.push(newForm);

        // NOTE(B6/S2): manual dual-update of node.data.forms + vm.activities.
        // deleteNode/deleteForm use rebuildActivitiesFromNodes instead; the two
        // paths differ on standalone (state-less) activities, so do NOT swap
        // this to rebuild without handling that case (see audit S2).
        // Add to centralization activities list if not already there
        if (!state.vm.activities.some((a) => a.uid === newForm.uid)) {
          state.vm.activities.push({
            uid: newForm.uid,
            name: newForm.label || newForm.name,
            type: newForm.type || "NORMAL",
          });
        }

        // Track as unsaved unless the caller explicitly marked it persisted
        // (e.g. attachExistingActivitiesToState paths reusing a saved uid).
        if (newForm.uid && !formData._alreadyPersisted && !state.unsavedActivityUIDs.includes(newForm.uid)) {
          state.unsavedActivityUIDs.push(newForm.uid);
        }

      }
    },
    addExistingActivitiesToState: (state, action) => {
      recordDiscrete(state);
      const { stateId, activities } = action.payload;
      const stateNode = findNode(state, stateId);
      if (stateNode) {
        if (!stateNode.data.forms) stateNode.data.forms = [];

        activities.forEach((act) => {
          if (!stateNode.data.forms.some((f) => f.uid === act.uid)) {
            stateNode.data.forms.push({
              id: act.uid,
              uid: act.uid,
              label: act.name,
              type: act.type
            });
          }
        });

      }
    },
    deleteForm: (state, action) => {
      recordDiscrete(state);
      const { stateId, formId } = action.payload;
      const stateNode = findNode(state, stateId);
      if (stateNode && stateNode.data.forms) {
        const removed = stateNode.data.forms.find((f) => f.id === formId);
        stateNode.data.forms = stateNode.data.forms.filter((f) => f.id !== formId);

        // Removing an activity from a state only DETACHES it — the activity
        // definition is never deleted here, and saving keeps it too (see
        // keep_orphan_activities in Toolbar). Keep it in the activities list
        // as a standalone (state-less) entry so it still appears in the
        // "Add Existing Activity" picker and can be re-attached later. To
        // delete an activity for real, use the Activities section.
        const activeActivities = rebuildActivitiesFromNodes(vmNodes(state));
        if (removed?.uid && !activeActivities.some((a) => a.uid === removed.uid)) {
          activeActivities.push({
            uid: removed.uid,
            name: removed.label,
            type: removed.type || 'NORMAL',
          });
        }
        state.vm.activities = activeActivities;
      }
    },
    undo: (state) => {
      if (!state.history.past.length) return;
      state.history.future.push(snapshotVM(state));
      const prev = state.history.past.pop();
      state.vm = prev;
      state._coalesceKey = null;
      state._dragging = false;
      pruneSelection(state);
    },
    redo: (state) => {
      if (!state.history.future.length) return;
      state.history.past.push(snapshotVM(state));
      const next = state.history.future.pop();
      state.vm = next;
      state._coalesceKey = null;
      state._dragging = false;
      pruneSelection(state);
    },
    // Payload may be a node object (every current dispatcher) or a bare id.
    // StateNode dispatches a synthesized { id, type, data } when an activity row
    // inside a state is clicked; only the id is kept now, and the selector
    // derives the live node from the vm — which is where that data came from.
    setSelectedNode: (state, action) => {
      const p = action.payload;
      state.selectedNodeId = typeof p === "string" ? p : (p?.id ?? null);
      state.selectedEdgeId = null;
    },
    setSelectedEdge: (state, action) => {
      const p = action.payload;
      state.selectedEdgeId = typeof p === "string" ? p : (p?.id ?? null);
      state.selectedNodeId = null;
    },
    setWorkflow: (state, action) => {
      state.workflow = action.payload;
    },
    updateWorkflowName: (state, action) => {
      if (state.workflow) {
        state.workflow.name = action.payload;
      }
    },
    // Workflow Data Fields
    setWorkflowDataFields: (state, action) => {
      state.workflowDataFields = action.payload || [];
    },
    addWorkflowDataField: (state, action) => {
      const newField = action.payload;
      let finalId = newField.id;
      let counter = 1;
      while (state.workflowDataFields.some(f => f.id === finalId)) {
        finalId = `${newField.id}_${counter}`;
        counter++;
      }
      state.workflowDataFields.push({ ...newField, id: finalId });
    },
    updateWorkflowDataField: (state, action) => {
      const { uid, data } = action.payload;
      const index = state.workflowDataFields.findIndex(f => f.uid === uid);
      if (index !== -1) {
        if (data.id && data.id !== state.workflowDataFields[index].id) {
          if (state.workflowDataFields.some(f => f.id === data.id && f.uid !== uid)) {
            const { id, ...otherData } = data;
            state.workflowDataFields[index] = { ...state.workflowDataFields[index], ...otherData };
            return;
          }
        }
        state.workflowDataFields[index] = { ...state.workflowDataFields[index], ...data };
      }
    },
    deleteWorkflowDataField: (state, action) => {
      state.workflowDataFields = state.workflowDataFields.filter(f => f.uid !== action.payload);
    },
    reorderWorkflowDataFields: (state, action) => {
      const { displayIndex, newIndex } = action.payload;
      const result = Array.from(state.workflowDataFields);
      const [removed] = result.splice(displayIndex, 1);
      result.splice(newIndex, 0, removed);
      state.workflowDataFields = result;
    },
    insertWorkflowDataFieldAfter: (state, action) => {
      // Mirrors insertActivityDataFieldAfter: the caller supplies an id already
      // minted version-wide-unique by the backend resolver, so no dedup
      // loop is needed.
      const { field, afterUid } = action.payload;
      const idx = state.workflowDataFields.findIndex(f => f.uid === afterUid);
      if (idx === -1) state.workflowDataFields.push(field);
      else state.workflowDataFields.splice(idx + 1, 0, field);
    },
    // RBAC Permissions
    setActivityPermissions: (state, action) => {
      state.activityPermissions = action.payload || [];
    },
    updateActivityPermission: (state, action) => {
      const { activityUid, data } = action.payload;
      const index = state.activityPermissions.findIndex(p => p.id === activityUid);
      if (index !== -1) {
        state.activityPermissions[index] = { ...state.activityPermissions[index], ...data };
      } else {
        state.activityPermissions.push({
          id: activityUid,
          allowed_groups: [],
          allowed_roles: [],
          dynamic_rbac_allowed: false,
          only_roles_positions: false,
          ...data
        });
      }
    },
    // Activity Data Fields
    setActivityDataFields: (state, action) => {
      state.activityDataFields = action.payload || [];
    },
    setActivityDataFieldsForActivity: (state, action) => {
      const { activityUid, fields } = action.payload;
      let activityEntry = state.activityDataFields.find(a => a.activity_uid === activityUid);
      if (activityEntry) {
        activityEntry.data_fields = fields || [];
      } else {
        state.activityDataFields.push({ activity_uid: activityUid, data_fields: fields || [] });
      }
    },
    addActivityDataField: (state, action) => {
      const { activityUid, field } = action.payload;
      let activityEntry = state.activityDataFields.find(a => a.activity_uid === activityUid);
      if (!activityEntry) {
        activityEntry = { activity_uid: activityUid, data_fields: [] };
        state.activityDataFields.push(activityEntry);
      }
      activityEntry.data_fields.push(field);
    },
    insertActivityDataFieldAfter: (state, action) => {
      const { activityUid, field, afterFieldUid } = action.payload;
      let activityEntry = state.activityDataFields.find(a => a.activity_uid === activityUid);
      if (!activityEntry) {
        activityEntry = { activity_uid: activityUid, data_fields: [] };
        state.activityDataFields.push(activityEntry);
      }
      const idx = activityEntry.data_fields.findIndex(f => f.uid === afterFieldUid);
      if (idx === -1) {
        activityEntry.data_fields.push(field);
      } else {
        activityEntry.data_fields.splice(idx + 1, 0, field);
      }
    },
    updateActivityDataField: (state, action) => {
      const { activityUid, fieldUid, data } = action.payload;
      const activityEntry = state.activityDataFields.find(a => a.activity_uid === activityUid);
      if (activityEntry) {
        const index = activityEntry.data_fields.findIndex(f => f.uid === fieldUid);
        if (index !== -1) {
          activityEntry.data_fields[index] = { ...activityEntry.data_fields[index], ...data };
        }
      }
    },
    deleteActivityDataField: (state, action) => {
      const { activityUid, fieldUid } = action.payload;
      const activityEntry = state.activityDataFields.find(a => a.activity_uid === activityUid);
      if (activityEntry) {
        activityEntry.data_fields = activityEntry.data_fields.filter(f => f.uid !== fieldUid);
      }
    },
    reorderActivityDataFields: (state, action) => {
      const { activityUid, displayIndex, newIndex } = action.payload;
      const activityEntry = state.activityDataFields.find(a => a.activity_uid === activityUid);
      if (activityEntry) {
        const result = Array.from(activityEntry.data_fields);
        const [removed] = result.splice(displayIndex, 1);
        result.splice(newIndex, 0, removed);
        activityEntry.data_fields = result;
      }
    },
    setCurrentOrg: (state, action) => {
      state.currentOrg = action.payload;
    },
    setCurrentPipelineGroup: (state, action) => {
      state.currentPipelineGroup = action.payload;
    },
    // Activity Triggers
    setActivityTriggers: (state, action) => {
      state.activityTriggers = action.payload || [];
    },
    setActivityTriggerFlow: (state, action) => {
      const { activityUid, nodes, edges } = action.payload;
      const index = state.activityTriggers.findIndex(t => t.activity_uid === activityUid);
      if (index !== -1) {
        state.activityTriggers[index] = { ...state.activityTriggers[index], nodes, edges };
      } else {
        state.activityTriggers.push({ activity_uid: activityUid, nodes, edges });
      }
    },
    setActivityTriggerSettings: (state, action) => {
      const { activityUid, settings } = action.payload;
      const index = state.activityTriggers.findIndex(t => t.activity_uid === activityUid);
      if (index !== -1) {
        state.activityTriggers[index] = { ...state.activityTriggers[index], settings };
      } else {
        state.activityTriggers.push({ activity_uid: activityUid, nodes: [], edges: [], settings });
      }
    },
    // Activity Prefills
    setActivityPrefills: (state, action) => {
      state.activityPrefills = action.payload || {};
    },
    setActivityPrefillFlow: (state, action) => {
      const { activityUid, nodes, edges } = action.payload;
      state.activityPrefills[activityUid] = { nodes, edges };
      state.activityPrefillFlow = { activityUid, nodes, edges };
    },
  },
});

export const {
  resetWorkflow,
  setGraph,
  undo,
  redo,
  setNodes,
  setEdges,
  graphNodesChanged,
  graphEdgesChanged,
  setActivities,
  addActivity,
  addNode,
  updateNodeData,
  deleteNode,
  updateEdgeData,
  addFormToState,
  addExistingActivitiesToState,
  deleteForm,
  markActivitiesSaved,
  setSelectedNode,
  setSelectedEdge,
  setWorkflow,
  updateWorkflowName,
  setWorkflowDataFields,
  addWorkflowDataField,
  updateWorkflowDataField,
  deleteWorkflowDataField,
  reorderWorkflowDataFields,
  insertWorkflowDataFieldAfter,
  setActivityPermissions,
  updateActivityPermission,
  setActivityDataFields,
  setActivityDataFieldsForActivity,
  addActivityDataField,
  insertActivityDataFieldAfter,
  updateActivityDataField,
  deleteActivityDataField,
  reorderActivityDataFields,
  setCurrentOrg,
  setCurrentPipelineGroup,
  setActivityTriggers,
  setActivityTriggerFlow,
  setActivityTriggerSettings,
  setActivityPrefills,
  setActivityPrefillFlow,
} = workflowSlice.actions;

export default workflowSlice.reducer;
